import { languageOptions } from '@efdigital/ll-addon-coaching/utils/languages';
import {
  QuizInputTemplate,
  QuizOutputTemplate,
} from '@efdigital/ll-addon-content/components/quiz/types';
import { addPageBreak } from '@efdigital/ll-addon-content/utils/quiz-css-class';
import {
  type Cohort,
  DataTypeQuiz,
  DataTypeSystem,
  generateRandomSlug,
  type PicklistItem,
  type Quiz,
  type QuizQuestion,
  type QuizQuestionChoiceOption,
  type UserGroup,
} from '@efdigital/ll-addon-data';
import { DataTypeCoaching } from '@efdigital/ll-addon-data/data-types';
import type { Coaching } from '@efdigital/ll-addon-data/types/coaching/coaching';
import type { CoachingConfig } from '@efdigital/ll-addon-data/types/coaching/coaching-config';
import { SingleChoiceQuestionManager } from '@efdigital/ll-admin-v2/components/quizzes/edit-quiz-questions/managers/choice-single';
import { PageBreakQuestionManager } from '@efdigital/ll-admin-v2/components/quizzes/edit-quiz-questions/managers/page-break';
import { TextQuestionManager } from '@efdigital/ll-admin-v2/components/quizzes/edit-quiz-questions/managers/text';
import { DEFAULT_RESPONSE } from '@efdigital/ll-admin-v2/components/quizzes/edit-quiz-questions/types/quiz-responses';
import { setAsyncBelongsTo } from '@efdigital/ll-admin-v2/data/async-setter';
import { queryPicklistItemsByGroupName } from '@efdigital/ll-admin-v2/data/builders/system/picklist-items';
import { COACH_POOL_CATEGORY } from '@efdigital/ll-admin-v2/data/builders/system/user-groups';
import {
  COACHING_INDUSTRIES_GROUP,
  COACHING_SENIORITY_GROUP,
} from '@efdigital/ll-admin-v2/data/builders/xceed/coach-profiles';
import { deepSave } from '@efdigital/ll-admin-v2/data/deep-save.ts';
import { getPaginationTotal } from '@efdigital/ll-admin-v2/helpers/get-pagination-total';
import { type OptionWithValue } from '@efdigital/ll-admin-v2/utils/quiz-choices';
import { dummyUserCoach } from '@efdigital/ll-admin-v2/utils/user-groups';
import type { Store } from '@warp-drive/core';
import { Type } from '@warp-drive/core/types/symbols';

type Context = { store: Store };

/**
 * Full coaching setup for a cohort: creates the Coaching record, a coach pool
 * (UserGroup), and an onboarding quiz, then links and saves everything.
 */
export async function generateCoaching(
  cohort: Cohort,
  coachingConfig: CoachingConfig,
  context: Context
): Promise<{ coaching: Coaching; pool: UserGroup; quiz: Quiz }> {
  const { store } = context;

  const coaching = store.createRecord<Coaching>(DataTypeCoaching.Coaching, {});
  setAsyncBelongsTo(coaching, 'coachingConfig', coachingConfig);

  const pool = await _createCoachPool(cohort, context);
  setAsyncBelongsTo(coaching, 'coachPool', pool);

  const quiz = await _createOnboardingQuiz(cohort, context);
  await deepSave(quiz, store, { maxDepth: 2 });
  setAsyncBelongsTo(coaching, 'quiz', quiz);

  await deepSave(coaching, store, { maxDepth: 1 });
  setAsyncBelongsTo(cohort, 'coaching', coaching);
  await deepSave(cohort, store, { maxDepth: 1 });

  return { coaching, pool, quiz };
}

/**
 * Provisions a coach pool for an existing Coaching that doesn't have one.
 * Used by the coaching route to silently backfill legacy coaching records.
 */
export async function generateCoachPool(
  cohort: Cohort,
  coaching: Coaching,
  context: Context
): Promise<UserGroup> {
  const { store } = context;
  const pool = await _createCoachPool(cohort, context);
  setAsyncBelongsTo(coaching, 'coachPool', pool);
  await deepSave(coaching, store, { maxDepth: 1 });
  return pool;
}

async function _createCoachPool(
  cohort: Cohort,
  { store }: Context
): Promise<UserGroup> {
  const pool = store.createRecord<UserGroup>(DataTypeSystem.UserGroup, {
    title: `${cohort.slug}-coach-pool`,
    scope: 'va',
    category: COACH_POOL_CATEGORY,
    expiresAfter: 0,
    userIds: [dummyUserCoach],
  });
  await deepSave(pool, store, { maxDepth: 1 });
  return pool;
}

async function _createOnboardingQuiz(
  cohort: Cohort,
  { store }: Context
): Promise<Quiz> {
  const program = await cohort.program;

  const quiz = store.createRecord<Quiz>(DataTypeQuiz.Quiz, {
    title: `${program?.title ?? ''} - ${cohort.title} - Coaching Onboarding`,
    language: 'en',
    markdown:
      'Tell us a little about yourself and what you want from coaching. Your answers help us match you with the right coach ahead of your chemistry call.',
    templateInput: QuizInputTemplate.Paged,
    templateOutput: QuizOutputTemplate.Default,
    templatePdf: '',
    scoringMethod: {
      [Type]: DataTypeQuiz.QuizScoringMethod,
      type: 'default',
      qid: '',
      hideZero: false,
      sort: 'none',
    },
    minSelections: 0,
    maxSelections: 0,
    needsApproval: false,
    needsAssignment: false,
    reportAnswers: true,
    reportAnswersInternal: true,
    preventEdits: false,
    category1: 'Coaching',
    category2: 'Onboarding',
    category3: cohort.region,
    category4: cohort.deliveryType,
    category5: '',
    questions: [],
    totals: [],
    categories: [],
    comparisons: [],
    systemLineage: 'll-admin-v2',
    css: 'hide-score',
  });

  const [industryQuestion, seniorityQuestion] = await Promise.all([
    _singleChoiceQuestion('Industry', COACHING_INDUSTRIES_GROUP, { store }),
    _singleChoiceQuestion('Seniority', COACHING_SENIORITY_GROUP, { store }),
  ]);

  const languageValues = languageOptions().map((option) => option.name);
  const languageQuestion1 = _buildSingleChoiceQuestion(
    'Language Preference 1',
    languageValues
  );
  const languageQuestion2 = _buildSingleChoiceQuestion(
    'Language Preference 2',
    languageValues,
    { required: false }
  );

  quiz.questions.push(
    _pageBreak('Required Information', { isFirstPage: true }),
    industryQuestion,
    seniorityQuestion,
    languageQuestion1,
    languageQuestion2,
    _pageBreak('About You'),
    _textQuestion('About you; your role and reason for choosing a coach now'),
    _textQuestion('What do you hope to get from your coaching?'),
    _textQuestion('What would you like to focus on in your initial sessions?'),
    _textQuestion('What are your key challenges and / or aspirations?'),
    _textQuestion(
      'What would you like to shift and what would success look like?'
    ),
    _textQuestion('What sort of person do you work well with?'),
    _textQuestion('What qualities are you looking for in a coach?'),
    _textQuestion(
      'Additional Context - Any additional information that would help us match you with the right coach?',
      { required: false }
    )
  );

  return quiz;
}

/**
 * Page 1 already exists implicitly before any page break, so giving it a
 * title only needs a labeled Meta question - actually breaking there would
 * push page 1's content onto an unlabeled page 2. Pass `isFirstPage: true`
 * for the leading title and omit it (default) for real page breaks.
 */
function _pageBreak(
  title: string = PageBreakQuestionManager.label,
  { isFirstPage = false }: { isFirstPage?: boolean } = {}
): QuizQuestion {
  return {
    [Type]: DataTypeQuiz.QuizQuestion,
    type: PageBreakQuestionManager.type,
    qid: generateRandomSlug(),
    title,
    markdown: '',
    css: isFirstPage ? '' : addPageBreak(''),
    crm: '',
    required: false,
    preview: false,
    reporting: {
      [Type]: DataTypeQuiz.QuizReportingQuestion,
      groupName: '',
      questionName: '',
    },
  };
}

function _textQuestion(
  title: string,
  {
    multiline = true,
    required = true,
  }: { multiline?: boolean; required?: boolean } = {}
): QuizQuestion {
  return {
    [Type]: DataTypeQuiz.QuizQuestion,
    type: TextQuestionManager.type,
    qid: generateRandomSlug(),
    title,
    markdown: '',
    css: '',
    crm: '',
    required,
    preview: true,
    ...structuredClone(TextQuestionManager.defaultData),
    text: {
      ...structuredClone(TextQuestionManager.defaultData.text!),
      multiline,
    },
    reporting: {
      [Type]: DataTypeQuiz.QuizReportingQuestion,
      groupName: '',
      questionName: '',
    },
  };
}

async function _singleChoiceQuestion(
  title: string,
  picklist: string,
  { store, required = true }: Context & { required?: boolean }
): Promise<QuizQuestion> {
  const items = await _fetchAllPicklistItems(picklist, store);
  const values = items
    .map((item) => item.title)
    .sort((a, b) => a.localeCompare(b));

  return _buildSingleChoiceQuestion(title, values, { required });
}

function _buildSingleChoiceQuestion(
  title: string,
  values: string[],
  { required = true }: { required?: boolean } = {}
): QuizQuestion {
  const question: QuizQuestion = {
    [Type]: DataTypeQuiz.QuizQuestion,
    type: SingleChoiceQuestionManager.type,
    qid: generateRandomSlug(),
    title,
    markdown: '',
    css: '',
    crm: '',
    required,
    preview: true,
    ...structuredClone(SingleChoiceQuestionManager.defaultData),
    reporting: {
      [Type]: DataTypeQuiz.QuizReportingQuestion,
      groupName: '',
      questionName: '',
    },
  };

  question.choice!.choices = values.map((value) => _singleChoice({ value }));
  question.choice!.format = 'dropdown';

  return question;
}

const PICKLIST_PAGE_SIZE = 100;

async function _fetchAllPicklistItems(
  groupName: string,
  store: Store
): Promise<PicklistItem[]> {
  const items: PicklistItem[] = [];
  let offset = 0;

  for (;;) {
    const result = await store.request(
      queryPicklistItemsByGroupName(groupName, {
        offset,
        limit: PICKLIST_PAGE_SIZE,
      })
    );
    items.push(...result.content.data);

    const total = getPaginationTotal(result.content);
    if (
      items.length >= total ||
      result.content.data.length < PICKLIST_PAGE_SIZE
    ) {
      break;
    }
    offset += PICKLIST_PAGE_SIZE;
  }

  return items;
}

function _singleChoice(option?: OptionWithValue): QuizQuestionChoiceOption {
  return {
    [Type]: DataTypeQuiz.QuizQuestionChoiceOption,
    oid: generateRandomSlug(),
    value: option?.value ?? '',
    score: option?.score ?? 0,
    category: '',
    additionalCategories: [],
    correct: false,
    response: DEFAULT_RESPONSE,
  };
}
